import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';

/** Manages procedural sound effects using the Web Audio API. */
export class AudioService {
    private static instance: AudioService;
    private audioCtx: AudioContext | null = null;

    private constructor() {}

    /** Returns the singleton instance, creating it on first call. */
    public static getInstance(): AudioService {
        if (!AudioService.instance) {
            AudioService.instance = new AudioService();
        }
        return AudioService.instance;
    }

    /**
     * Subscribes to the event bus and plays a tectonic rumble whenever
     * `world:ground-updated` fires.
     */
    public subscribeToEvents(pubsub: PubSub<GameEvents>): void {
        pubsub.subscribe('world:ground-updated', () => this.playTectonicRumble());
    }

    /**
     * Initializes the AudioContext on first call (required by browsers before
     * any sound can be produced) and resumes it if suspended.
     */
    public init(): void {
        if (!this.audioCtx) {
            this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        if (this.audioCtx.state === 'suspended') {
            this.audioCtx.resume();
        }
    }

    /**
     * Plays a deep, procedural tectonic rumble composed of filtered white noise
     * and a sub-bass sine pulse.
     *
     * @param type - `'raise'` produces a slightly higher pitch; `'lower'` is deeper.
     */
    public playTectonicRumble(type: 'raise' | 'lower' = 'raise'): void {
        this.init();
        if (!this.audioCtx) return;

        const ctx = this.audioCtx;
        const now = ctx.currentTime;

        const bufferSize = ctx.sampleRate * 0.5;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
        }

        const source = ctx.createBufferSource();
        source.buffer = buffer;

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(type === 'raise' ? 150 : 80, now);
        filter.Q.setValueAtTime(10, now);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.2, now + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

        source.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        source.start(now);
        source.stop(now + 0.5);

        const sub = ctx.createOscillator();
        const subGain = ctx.createGain();
        sub.type = 'sine';
        sub.frequency.setValueAtTime(type === 'raise' ? 60 : 40, now);

        subGain.gain.setValueAtTime(0, now);
        subGain.gain.linearRampToValueAtTime(0.3, now + 0.1);
        subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

        sub.connect(subGain);
        subGain.connect(ctx.destination);
        sub.start(now);
        sub.stop(now + 0.3);
    }
}
